import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of, throwError, timer} from 'rxjs';
import {catchError, delayWhen, map, switchMap} from 'rxjs/operators';
import {
  RpcRequest,
  LoginParams,
  FactoryParams,
  PtzParams,
} from '../../shared/models/ptz-commands';

interface LoginResponse {
  session: string;
}

interface FactoryResponse {
  result: number;
}

@Injectable({providedIn: 'root'})
export class CameraService {
  constructor(private http: HttpClient) {
  }

  /**
   * Pan the camera: performs login, factory.instance, start, wait, and stop.
   * @param baseUrl Device base URL (e.g. "http://188.59.180.172:180").
   * @param direction PTZ direction: 'Left' | 'Right' | 'Up' | 'Down'.
   */
  pan(baseUrl: string, direction: 'Left' | 'Right' | 'Up' | 'Down'): Observable<void> {
    const rpcEndpoint = `${baseUrl}/RPC2`;
    const loginEndpoint = `${rpcEndpoint}_Login`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    });

    return this.login(loginEndpoint, headers)
      .pipe(
        switchMap(session => this.getFactoryObject(rpcEndpoint, session, headers)),
        switchMap(({session, object}) => this.startMove(rpcEndpoint, session, object, direction, headers)),
        delayWhen(() => timer(1500)),
        switchMap(({session, object}) => this.stopMove(rpcEndpoint, session, object, direction, headers)),
        catchError(err => {
          console.error('Error during PTZ movement:', err);
          return throwError(() => err);
        })
      );
  }

  private login(
    loginUrl: string,
    headers: HttpHeaders
  ): Observable<string> {
    const body: RpcRequest<LoginParams> = {
      method: 'global.login',
      params: {
        userName: 'admin',
        loginType: 'Direct',
        clientType: 'NetKeyboard',
        authorityType: 'Default',
        passwordType: 'Default',
        password: 'not_used'
      },
      id: 1,
      session: 0
    };

    return this.http.post<LoginResponse>(loginUrl, JSON.stringify(body), {headers})
      .pipe(
        switchMap(res => {
          // console.log('Login session:', res.session);
          return of(res.session);
        }),
        catchError(err => {
          console.error('Login failed:', err);
          return throwError(() => err);
        })
      );
  }

  private getFactoryObject(
    rpcUrl: string,
    session: string,
    headers: HttpHeaders
  ): Observable<{ session: string; object: number }> {
    const body: RpcRequest<FactoryParams> = {
      method: 'ptz.factory.instance',
      params: {channel: 0},
      id: 214,
      session
    };

    return this.http.post<FactoryResponse>(rpcUrl, JSON.stringify(body), {headers})
      .pipe(
        switchMap(res => {
          // console.log('Factory instance object:', res.result);
          return of({session, object: res.result});
        }),
        catchError(err => {
          console.error('Fetching factory.instance failed:', err);
          return throwError(() => err);
        })
      );
  }

  private startMove(
    rpcUrl: string,
    session: string,
    object: number,
    direction: string,
    headers: HttpHeaders
  ): Observable<{ session: string; object: number }> {
    const body: RpcRequest<PtzParams> = {
      method: 'ptz.start',
      params: {code: direction, arg1: 5, arg2: 0, arg3: 0},
      id: 322,
      session,
      object,
      seq: Date.now()
    };

    return this.http.post(rpcUrl, JSON.stringify(body), {headers})
      .pipe(
        switchMap(() => of({session, object})),
        catchError(err => {
          console.error('ptz.start failed:', err);
          return throwError(() => err);
        })
      );
  }

  private stopMove(
    rpcUrl: string,
    session: string,
    object: number,
    direction: string,
    headers: HttpHeaders
  ): Observable<void> {
    const body: RpcRequest<PtzParams> = {
      method: 'ptz.stop',
      params: {code: direction, arg1: 5, arg2: 0, arg3: 0},
      id: 323,
      session,
      object,
      seq: Date.now()
    };

    return this.http.post(rpcUrl, JSON.stringify(body), {headers})
      .pipe(
        catchError(err => {
          console.error('ptz.stop failed:', err);
          return throwError(() => err);
        }),
        switchMap(() => {
          // console.log('PTZ movement completed');
          return of(void 0);
        })
      );
  }
}
